/**
 * A question with several answers (Outlook: "This occurrence / The whole series", "Send update / Don't send"),
 * asked from async code: `const answer = await ask({...})` resolves with the chosen value or undefined (Cancel/Esc).
 */
import { useI18n } from '@platform/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@platform/ui';
import { useCallback, useState, type ReactNode } from 'react';

export interface ChoiceOptions<T extends string> {
  title: string;
  description: string;
  choices: readonly { value: T; label: string; primary?: boolean }[];
}

interface Open {
  options: ChoiceOptions<string>;
  resolve: (value: string | undefined) => void;
}

export function useChoice(): [ReactNode, <T extends string>(options: ChoiceOptions<T>) => Promise<T | undefined>] {
  const { t } = useI18n();
  const [open, setOpen] = useState<Open>();
  const ask = useCallback(
    <T extends string>(options: ChoiceOptions<T>) =>
      new Promise<T | undefined>((resolve) => setOpen({ options, resolve: resolve as (value: string | undefined) => void })),
    [],
  );
  const finish = (value: string | undefined) => {
    open?.resolve(value);
    setOpen(undefined);
  };
  const element = open && (
    <Dialog open onOpenChange={(next) => !next && finish(undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{open.options.title}</DialogTitle>
          <DialogDescription>{open.options.description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={() => finish(undefined)}>
            {t('common.cancel')}
          </Button>
          {open.options.choices.map((choice) => (
            <Button key={choice.value} variant={choice.primary ? 'default' : 'outline'} onClick={() => finish(choice.value)}>
              {choice.label}
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return [element, ask];
}
